import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface CollapsibleCardProps {
  title: ReactNode;
  /** Resumo mostrado no cabeçalho quando recolhido (ex.: "4 documentos"). */
  summary?: ReactNode;
  defaultOpen?: boolean;
  /** Modo controlado (opcional): o pai coordena qual card está aberto. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

/**
 * Card recolhível com resumo no cabeçalho. Reduz o peso visual de seções densas
 * (mostra só o resumo até o usuário expandir). Funciona controlado (``open`` +
 * ``onOpenChange``) ou não controlado (``defaultOpen``). Não há Accordion no DS.
 */
export function CollapsibleCard({
  title,
  summary,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  children,
}: CollapsibleCardProps) {
  const [interno, setInterno] = useState(defaultOpen);
  const controlado = openProp !== undefined;
  const open = controlado ? openProp : interno;
  const cabecalhoId = useId();
  const conteudoId = useId();

  const alternar = () => {
    const proximo = !open;
    if (!controlado) setInterno(proximo);
    onOpenChange?.(proximo);
  };

  return (
    <Card>
      <Button
        type="button"
        variant="ghost"
        onClick={alternar}
        aria-expanded={open}
        aria-controls={conteudoId}
        id={cabecalhoId}
        className="flex h-auto w-full items-center justify-between gap-3 rounded-lg px-6 py-4 text-left hover:bg-transparent"
      >
        <span className="font-semibold">{title}</span>
        <span className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
          {!open && summary}
          <ChevronDown size={16} className={cn('shrink-0 transition-transform', open && 'rotate-180')} />
        </span>
      </Button>
      {/* Sempre montado (escondido com hidden): o aria-controls referencia um id
          que existe no DOM mesmo fechado, e o estado interno dos filhos sobrevive
          ao colapsar. */}
      <CardContent
        id={conteudoId}
        role="region"
        aria-labelledby={cabecalhoId}
        hidden={!open}
        className="space-y-4 pt-0"
      >
        {children}
      </CardContent>
    </Card>
  );
}
